import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Default module access - all enabled
const DEFAULT_MODULE_ACCESS = {
  inventory: true,
  sales: true,
  wholesale: true,
  accounting: true,
  treasury: true,
  reconditioning: true,
  evaluations: true,
  crm: true,
  reports: true,
  budget: true,
  deliveries: true,
  fb_marketplace: true,
  // Additional navigation modules
  president: true,
  manager: true,
  deposits: true,
  sales_board: true,
  marketing_ai: true
};

const ModuleAccessContext = createContext({
  moduleAccess: DEFAULT_MODULE_ACCESS,
  hasFullAccess: false,
  loading: true,
  hasAccess: (moduleId) => true,
  refreshAccess: () => {}
});

export const useModuleAccess = () => {
  const context = useContext(ModuleAccessContext);
  if (!context) {
    throw new Error('useModuleAccess must be used within a ModuleAccessProvider');
  }
  return context;
};

export const ModuleAccessProvider = ({ children }) => {
  const { user } = useAuth();
  const [moduleAccess, setModuleAccess] = useState(DEFAULT_MODULE_ACCESS);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchModuleAccess = useCallback(async () => {
    if (!user) {
      setModuleAccess(DEFAULT_MODULE_ACCESS);
      setHasFullAccess(false);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/user/module-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setModuleAccess(response.data.moduleAccess || DEFAULT_MODULE_ACCESS);
      setHasFullAccess(response.data.hasFullAccess || false);
    } catch (error) {
      console.error('Error fetching module access:', error);
      // On error, default to all access enabled
      setModuleAccess(DEFAULT_MODULE_ACCESS);
      setHasFullAccess(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchModuleAccess();
  }, [fetchModuleAccess]);

  // Helper function to check if user has access to a specific module
  const hasAccess = useCallback((moduleId) => {
    if (hasFullAccess) return true;
    return moduleAccess[moduleId] !== false; // Default to true if not specified
  }, [moduleAccess, hasFullAccess]);

  const value = {
    moduleAccess,
    hasFullAccess,
    loading,
    hasAccess,
    refreshAccess: fetchModuleAccess
  };

  return (
    <ModuleAccessContext.Provider value={value}>
      {children}
    </ModuleAccessContext.Provider>
  );
};

export default ModuleAccessContext;
